import { FormControl } from '@chakra-ui/react';
import { useClassFiltersStore } from '@stores/classes';
import { useListCoaches } from 'hooks/useApiQueries';
import { useCoachStudioParams } from 'hooks/useCoachStudioParams';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select } from 'chakra-react-select';
import { ChakraStylesConfig } from 'chakra-react-select';

interface CoachOption {
  label: string;
  value: number;
}

export function ClassesFiltersCoaches() {
  const { data: coaches } = useListCoaches();
  const coachIds = useClassFiltersStore((state) => state.coachIds);
  const setCoachIds = useClassFiltersStore((state) => state.setCoachIds);

  const { isCoachUrl, id } = useCoachStudioParams();
  const shortCodeCoachId = mondayMindbody?.coach?.length && parseInt(mondayMindbody.coach, 10);
  const navigate = useNavigate();

  // Flag pour empêcher le useLayoutEffect de récraser la sélection manuelle
  const isManualChange = useRef(false);

  useLayoutEffect(() => {

    // Ne pas forcer si l'utilisateur vient de faire un changement manuel
    if (!isManualChange.current) {
      if (shortCodeCoachId) {

        setCoachIds([shortCodeCoachId]);
      } else if (isCoachUrl) {

        setCoachIds([id]);
      }
    }
    // Reset le flag après chaque render
    isManualChange.current = false;
    // Ne PAS réinitialiser les coaches si aucun shortcode ou URL n'est présent
    // Cela permettait de garder la sélection manuelle de l'utilisateur
  }, [isCoachUrl, id, setCoachIds, shortCodeCoachId]);

  const options: CoachOption[] = useMemo(() => {
    if (!coaches) return [];

    let filteredCoaches = coaches;
    if (shortCodeCoachId) {
      filteredCoaches = coaches.filter((coach) => coach.Id === shortCodeCoachId);
    } else if (mondayMindbody?.siteId) {
      filteredCoaches = coaches.filter((coach) => mondayMindbody.siteId === String(coach.SiteId));
    }

    return filteredCoaches
      .sort((previous, next) => previous.FirstName.localeCompare(next.FirstName))
      .map((coach) => ({ label: coach.FirstName, value: coach.Id }));
  }, [coaches, shortCodeCoachId]);

  const values = options.filter((option) => coachIds.includes(option.value));


  const chakraStyles: ChakraStylesConfig<CoachOption, true> = {
    container: (provided) => ({
      ...provided,
      minWidth: '200px',
    }),
    control: (provided) => ({
      ...provided,
      backgroundColor: 'white',
      borderColor: 'gray.200',
      _hover: {
        borderColor: 'primary.500',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? 'primary.500' : 'white',
      color: state.isSelected ? 'white' : 'gray.700',
      _hover: {
        backgroundColor: state.isSelected ? 'primary.600' : 'gray.100',
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: 'primary.100',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'primary.700',
    }),
  };

  return (
    <FormControl>
      <Select<CoachOption, true>
        size="sm"
        isMulti
        name="coaches"
        options={options}
        placeholder="Sélectionner un ou plusieurs coaches"
        closeMenuOnSelect={false}
        value={values}
        onChange={(options) => {
          const selectedIds = options ? options.map((option) => option.value) : [];

          // Marquer ce changement comme manuel
          isManualChange.current = true;

          // Mettre à jour le store AVANT de naviguer
          setCoachIds(selectedIds);

          // Si on est sur une URL /coach/:id et que l'utilisateur modifie la sélection
          // on nettoie l'URL pour repasser en mode "normal"
          if (isCoachUrl) {
            navigate('/', { replace: true });
          }
        }}
        chakraStyles={chakraStyles}
        isDisabled={!!shortCodeCoachId}
      />
    </FormControl>
  );
}
